import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { supabase } from "@/integrations/supabase/client";
import { getPersonaFromPath, getPersonaFromRole } from "@/lib/persona";
import type { AppRole } from "@/lib/marketplace-auth";

/**
 * Fires PostHog pageviews on every route change and sets persona context.
 * Also identifies the user when Supabase auth resolves.
 *
 * Must be rendered inside <BrowserRouter> and <PostHogProvider>.
 */
export function PostHogPageTracker() {
  const location = useLocation();
  const posthog = usePostHog();

  // Pageview + route persona on every navigation
  useEffect(() => {
    if (!posthog) return;
    const persona = getPersonaFromPath(location.pathname);
    posthog.capture("$pageview", {
      $current_url: window.location.href,
      persona,
    });
    posthog.register({ current_persona: persona });
  }, [location.pathname, posthog]);

  // Identify user when Supabase session resolves
  useEffect(() => {
    if (!posthog) return;

    const identifyUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: roleRows } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      const roles = (roleRows ?? []).map((r) => r.role as AppRole);
      const persona = getPersonaFromRole(roles) ?? getPersonaFromPath(location.pathname);

      posthog.identify(user.id, {
        email: user.email,
        persona,
        roles,
        created_at: user.created_at,
      });
      posthog.register({ current_persona: persona });
    };

    identifyUser();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === "SIGNED_IN" && session?.user) {
        identifyUser();
      }
      if (event === "SIGNED_OUT") {
        posthog.reset();
      }
    });

    return () => subscription.unsubscribe();
  }, [posthog]);

  return null;
}
